import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiEye, FiRotateCcw } from 'react-icons/fi';
import { listCaseVersions, getCaseVersion } from '../../services/assistantClient';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';

/** 바뀐 칸 키 → 화면 이름(번역 키). 키는 서버 tuningCase/store.js 의 VERSION_FIELDS 와 같다 */
export const changedLabel = (key, t) => t(`assistant:caseVersion.field.${key}`, { defaultValue: key });

/**
 * 사례의 과정 이력 + 지난 판(2026-10-24 사용자 요청 — 과거 버전 최대 10개 보존).
 * 이력 줄마다 그 저장에서 바뀐 칸을 보이고, 지난 판은 「보기」(문서로) · 「이 판으로 되돌리기」(편집 화면에 그 판을 채워 연다 —
 * 저장은 평소처럼 사람이 한다: 변조 검증·이력이 같은 길을 지난다). 주인에게만 그린다(서버도 주인만 준다).
 */
export default function CaseVersions({ caseId, history, mine, viewingId, onView, onRestore }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [state, setState] = useState({ loading: false, versions: null, keep: 10, error: null });
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        if (!mine || !caseId) return;
        setState(s => ({ ...s, loading: true, error: null }));
        try {
            const r = await listCaseVersions(caseId);
            setState({ loading: false, versions: r.versions || [], keep: r.keep || 10, error: null });
        } catch (err) {
            setState({ loading: false, versions: [], keep: 10, error: formatApiError(err, t) });
        }
    }, [caseId, mine, t]);
    // 펼칠 때 읽는다 — 사례를 고를 때마다 부르지 않게. 다른 사례로 바뀌면 다시
    useEffect(() => { setState({ loading: false, versions: null, keep: 10, error: null }); }, [caseId]);
    useEffect(() => { if (open && state.versions === null && !state.loading) load(); }, [open, state.versions, state.loading, load]);
    // 같은 사례를 다시 저장하면(이력이 늘면) 판 목록도 다시
    const histLen = (history || []).length;
    useEffect(() => { if (open) load(); }, [histLen]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchVersion = async (v, then) => {
        setBusyId(v.id);
        try { then(await getCaseVersion(caseId, v.id)); }
        catch (err) { setState(s => ({ ...s, error: formatApiError(err, t) })); }
        finally { setBusyId(null); }
    };

    const actorText = (actor, via) => `${t(`assistant:caseList.historyActor.${actor === 'ai' ? 'ai' : 'user'}`)}${via === 'chat' ? ` · ${t('assistant:caseList.historyChat')}` : ''}`;
    if (!histLen && !mine) return null;

    return (
        <details className="tcase-history" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
            <summary>{t('assistant:caseList.history', { n: histLen })}</summary>
            <ul>
                {[...(history || [])].reverse().map((h, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`${h.at}-${i}`}>
                        <span className="mono">{storedTime(h.at, 'min')}</span>{' '}
                        <b>{actorText(h.actor, h.via)}</b>{' '}
                        {t(`assistant:caseList.historyAction.${h.action === 'create' ? 'create' : 'edit'}`)}
                        {h.note ? ` — ${h.note}` : ''}
                        {Array.isArray(h.changed) && h.changed.length ? (
                            <span className="tcase-histChanged"> · {t('assistant:caseVersion.changed')}: {h.changed.map(k => changedLabel(k, t)).join(', ')}</span>
                        ) : null}
                    </li>
                ))}
            </ul>
            {mine && (
                <div className="tcase-versions">
                    <div className="tcase-versionsHead">
                        <b>{t('assistant:caseVersion.title')}</b>
                        <span>{t('assistant:caseVersion.hint', { keep: state.keep })}</span>
                    </div>
                    {state.error ? <div className="tcase-warn">{state.error}</div> : null}
                    {state.loading && state.versions === null ? <div className="tcase-empty">{t('common:state.loading')}</div> : null}
                    {state.versions && state.versions.length === 0 ? <div className="tcase-versionsEmpty">{t('assistant:caseVersion.none')}</div> : null}
                    {state.versions && state.versions.length > 0 && (
                        <table className="tcase-versionsTable">
                            <tbody>
                                {state.versions.map(v => (
                                    <tr key={v.id} className={viewingId === v.id ? 'on' : undefined}>
                                        <td className="mono">#{v.versionNo}</td>
                                        <td className="mono">{storedTime(v.savedAt, 'min')}</td>
                                        <td>{actorText(v.actor)}</td>
                                        <td className="tcase-versionsChanged" title={v.changed.map(k => changedLabel(k, t)).join(', ')}>
                                            {t('assistant:caseVersion.changedAfter')}: {v.changed.map(k => changedLabel(k, t)).join(', ') || '-'}
                                        </td>
                                        <td className="tcase-versionsAct">
                                            <button type="button" className="tcase-insert" disabled={busyId === v.id}
                                                onClick={() => fetchVersion(v, onView)}>
                                                <FiEye size={11} /> {t('assistant:caseVersion.view')}
                                            </button>
                                            <button type="button" className="tcase-insert" disabled={busyId === v.id}
                                                title={t('assistant:caseVersion.restoreHint')}
                                                onClick={() => fetchVersion(v, onRestore)}>
                                                <FiRotateCcw size={11} /> {t('assistant:caseVersion.restore')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </details>
    );
}

CaseVersions.propTypes = {
    caseId: PropTypes.string,
    history: PropTypes.array,
    mine: PropTypes.bool,
    viewingId: PropTypes.string,
    onView: PropTypes.func.isRequired,
    onRestore: PropTypes.func.isRequired,
};
